import React, { Component } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Box, FormControl, Input, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import { hideAnnotationsForUser, updateUserAnnotationVisibility } from '../../../actions/view';
import { getUsersWithAnnotationsOnCurrentPageExceptCurrentUser } from '../../../selectors/annotations';
import { RootState } from '../../../store';
import { User } from '../../../store/users';
import { AnnotationVisibilitySettings } from '../../../store/view';
import UserChip from './UserChip';

export interface DisplayUser {
  id: string;
  name: string;
  color: string;
  visible: boolean;
}

const DEFAULT_ANNOTATION_COLOR = '#009DEC';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MENU_PROPS = {
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  },
};

function renderUserMenuItem(user: DisplayUser) {
  return (
    <MenuItem
      key={user.id}
      value={user.id}
      style={{
        color: user.color,
      }}
    >
      {user.name}
    </MenuItem>
  );
}

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

class VisibleUserSelect extends Component<Props> {
  onVisibleUsersChange = (event: SelectChangeEvent<readonly string[]>) => {
    this.props.updateUserAnnotationVisibility(event.target.value as readonly string[]);
  };

  onHideUserAnnotations = (userEmail: string) => {
    this.props.hideAnnotationsForUser(userEmail);
  };

  render() {
    const { users } = this.props;
    const visibleUsers = users.filter((user) => user.visible);

    return (
      <FormControl sx={{ width: '100%' }}>
        <InputLabel htmlFor="select-multiple-chip">Users</InputLabel>
        <Select
          id="userSelect"
          multiple
          value={visibleUsers.map((user) => user.id)}
          onChange={this.onVisibleUsersChange}
          input={<Input id="select-multiple-chip" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              {(selected as string[]).map((id) => (
                <UserChip
                  key={id}
                  user={users.find((user) => user.id === id)!}
                  sx={{
                    m: 0.25,
                    '& .Chip-deleteIcon': {
                      color: '#303030',
                      '&:hover': {
                        color: '#3a3a3a',
                      },
                    },
                  }}
                  onHideClick={this.onHideUserAnnotations}
                />
              ))}
            </Box>
          )}
          MenuProps={MENU_PROPS}
          label="Users"
        >
          {users.map(renderUserMenuItem)}
        </Select>
      </FormControl>
    );
  }
}

function mapStateToProps(state: RootState) {
  const userAnnotationsVisible = state.view.userAnnotationsVisible as AnnotationVisibilitySettings;

  const users: DisplayUser[] = (getUsersWithAnnotationsOnCurrentPageExceptCurrentUser(state) as User[]).map((user) => ({
    id: user.email,
    name: `${user.firstName} ${user.lastName}`,
    color: user.annotationColor || DEFAULT_ANNOTATION_COLOR,
    visible: userAnnotationsVisible === 'all' || userAnnotationsVisible.includes(user.email),
  }));

  return { users };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators({ updateUserAnnotationVisibility, hideAnnotationsForUser }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(VisibleUserSelect);
